import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Card, Alert, Typography, Space, Divider } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons';
import staffAuthManager from '../firebase/staffAuthService';

const { Title, Text } = Typography;

const StaffLogin = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated or handle bypass mode
  useEffect(() => {
    const checkAuthState = async () => {
      // Check if bypass mode is enabled
      if (staffAuthManager.isBypassMode()) {
        setLoading(true);
        try {
          const result = await staffAuthManager.bypassLogin();
          if (result.success) {
            const from = location.state?.from?.pathname || '/staff-portal';
            navigate(from, { replace: true });
          }
        } catch (err) {
          setError('Bypass mode error: ' + err.message);
        } finally {
          setLoading(false);
        }
        return;
      }

      // Normal auth check
      if (staffAuthManager.isAuthenticated()) {
        const from = location.state?.from?.pathname || '/staff-portal';
        navigate(from, { replace: true });
      }
    };

    checkAuthState();
  }, [navigate, location]);

  const handleLogin = async (values) => {
    setLoading(true);
    setError(null);

    try {
      const result = await staffAuthManager.employeeLogin(values.email, values.password);
      
      if (result.success) {
        // Redirect to intended page or staff portal
        const from = location.state?.from?.pathname || '/staff-portal';
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginFailed = (errorInfo) => {
    console.log('Login form validation failed:', errorInfo);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '40px 32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64,
            height: 64,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            color: 'white',
            fontSize: '24px'
          }}>
            <UserOutlined />
          </div>
          <Title level={2} style={{ margin: 0, color: '#1f2937' }}>
            Staff Portal
          </Title>
          <Text type="secondary">
            Sign in to access your attendance portal
          </Text>
          {staffAuthManager.isBypassMode() && (
            <div style={{ 
              marginTop: 12, 
              padding: '8px 12px', 
              background: '#fff2e8', 
              border: '1px solid #ffb366',
              borderRadius: '6px',
              color: '#d46b08'
            }}>
              <Text strong style={{ color: '#d46b08', fontSize: '12px' }}>
                🔧 BYPASS MODE ACTIVE - Development Testing
              </Text>
            </div>
          )}
        </div>

        {error && (
          <Alert
            message="Login Failed"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
            closable
            onClose={() => setError(null)}
          />
        )}

        <Form
          form={form}
          name="staff-login"
          onFinish={handleLogin}
          onFinishFailed={handleLoginFailed}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter your email address' },
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#9ca3af' }} />}
              placeholder="Enter your email"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter your password' },
              { min: 6, message: 'Password must be at least 6 characters' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#9ca3af' }} />}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              icon={<LoginOutlined />}
              style={{
                height: 48,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Form.Item>
        </Form>

        <Divider style={{ margin: '24px 0' }} />

        <div style={{ textAlign: 'center' }}>
          <Space direction="vertical" size="small">
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Need help accessing your account?
            </Text>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              Contact your administrator for assistance
            </Text>
          </Space>
        </div>

        <div style={{ 
          textAlign: 'center', 
          marginTop: 24, 
          paddingTop: 16, 
          borderTop: '1px solid #f0f0f0' 
        }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            © 2024 Beema Scaffolding SG. All rights reserved.
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default StaffLogin;
